import { useEffect, useState } from "react";
import { ListChecks, Plus, Pencil, Check, X, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useLookups, invalidateLookups } from "../lib/lookups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "./ConfirmDialog";

export default function LookupsPanel() {
  const all = useLookups();
  const keys = Object.keys(all);
  const [key, setKey] = useState("subsector");
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [del, setDel] = useState(null);
  const list = all[key];
  const values = list?.values || [];

  useEffect(() => { if (keys.length && !all[key]) setKey(keys[0]); }, [keys, all, key]);

  const persist = async (next, extra = {}) => {
    try {
      await api.put(`/lookups/${key}`, { values: next, ...extra });
      invalidateLookups();
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); return false; }
  };
  const add = async () => {
    const v = name.trim(); if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) return toast.error("Valore già presente");
    if (await persist([...values, v])) { setName(""); toast.success("Valore aggiunto"); }
  };
  const rename = async (old) => {
    const v = editVal.trim(); if (!v || v === old) return setEditing(null);
    if (await persist(values.map((x) => (x === old ? v : x)), { rename_from: old, rename_to: v })) { setEditing(null); toast.success("Valore rinominato su tutti i record"); }
  };
  const move = async (i, d) => { const n = [...values]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; await persist(n); };
  const remove = async () => { if (await persist(values.filter((x) => x !== del))) { toast.success("Valore eliminato dalla lista"); } setDel(null); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="lookups-panel">
      <p className="text-sm font-heading font-semibold flex items-center gap-2 mb-1"><ListChecks className="w-4 h-4 text-teal-600" /> Liste valori dei campi</p>
      <p className="text-sm text-slate-500 mb-4">Gestisci i valori dei menu a tendina del CRM (settori, sottosettori, paesi, fonti, servizi, fasce, seniority…). Le modifiche sono immediate in tutti i form e filtri, senza ripubblicare. Ogni utente può anche aggiungere un valore direttamente dal form con "Aggiungi nuovo valore…".</p>
      <Select value={key} onValueChange={setKey}>
        <SelectTrigger data-testid="lookups-key-select" className="h-10 mb-3"><SelectValue placeholder="Scegli lista" /></SelectTrigger>
        <SelectContent>{keys.map((k) => <SelectItem key={k} value={k} data-testid={`lookups-key-${k}`}>{all[k].label} <span className="text-slate-400 text-xs">· {all[k].values.length}</span></SelectItem>)}</SelectContent>
      </Select>
      {list && <p className="text-xs text-slate-400 mb-3" data-testid="lookups-usage">Usato in: {list.usage}</p>}
      <div className="flex gap-2 mb-4">
        <Input data-testid="lookup-new-value" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`Nuovo valore per "${list?.label || ""}"`} className="h-10" />
        <Button data-testid="lookup-add-btn" onClick={add} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5 shrink-0 min-h-[40px]"><Plus className="w-4 h-4" /> <span className="hidden sm:inline">Aggiungi</span></Button>
      </div>
      <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg max-h-[420px] overflow-y-auto">
        {values.length === 0 && <p className="text-sm text-slate-400 px-3 py-4 text-center">Nessun valore</p>}
        {values.map((v, i) => (
          <div key={v} data-testid={`lookup-row-${i}`} className="flex items-center gap-1.5 sm:gap-2 px-3 py-2">
            {editing === v ? (
              <>
                <Input data-testid={`lookup-edit-input-${i}`} value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8 flex-1" autoFocus onKeyDown={(e) => { if (e.key === "Enter") rename(v); if (e.key === "Escape") setEditing(null); }} />
                <button data-testid={`lookup-save-${i}`} onClick={() => rename(v)} className="icon-btn p-1.5 rounded hover:bg-emerald-50 text-emerald-600"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEditing(null)} className="icon-btn p-1.5 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <span className="text-sm text-slate-800 flex-1 min-w-0 truncate" data-testid={`lookup-value-${i}`}>{v}</span>
                <button title="Sposta su" onClick={() => move(i, -1)} disabled={i === 0} className="icon-btn p-1.5 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button title="Sposta giù" onClick={() => move(i, 1)} disabled={i === values.length - 1} className="icon-btn p-1.5 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button data-testid={`lookup-rename-${i}`} title="Rinomina" onClick={() => { setEditing(v); setEditVal(v); }} className="icon-btn p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`lookup-delete-${i}`} title="Elimina" onClick={() => setDel(v)} className="icon-btn p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} onConfirm={remove} title="Elimina valore"
        description={`Rimuovere "${del}" dalla lista "${list?.label}"? I record che lo usano mantengono il valore salvato.`} />
    </div>
  );
}
